// src/pages/modules/DataEntry/ApproveCredit/ApproveCredit.tsx
// ฟอร์มอนุมัติสินเชื่อ — เลือกลูกค้าจาก data_a → กรอกยอด → ย้ายไป data_b

import React from 'react'
import { Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap'
import { AuthContext } from '../../../../_services/firebase'
import { getAllCustomers, approveCustomer } from '../../../../_services/customer.service'
import { calcLoan, formatCurrency, formatDate, toastDanger, toastSuccess } from '../../../../_utils'
import ConfirmAlert from '../../../../components/ConfirmAlert/ConfirmAlert'
import type { CustomerReg, LoanData } from '../../../../_types'
import { dbRead } from '../../../../_services/firebase'
import PhotoGallery from '../../../../components/PhotoGallery/PhotoGallery'
import { DB_SCORES } from '../../../../_config/db_paths'

export default function ApproveCredit() {
  const { currentUser } = React.useContext(AuthContext)
  const uid = currentUser?.uid || ''

  const [loading,     setLoading]     = React.useState(true)
  const [customers,   setCustomers]   = React.useState<CustomerReg[]>([])
  const [selected,    setSelected]    = React.useState<CustomerReg | null>(null)
  const [scores,      setScores]      = React.useState<any>(null)

  // loan form
  const [actualAmount,     setActualAmount]     = React.useState('')
  const [interestRate,     setInterestRate]      = React.useState('0')
  const [installmentDays,  setInstallmentDays]   = React.useState('30')
  const [purpose,          setPurpose]           = React.useState('')
  const [zone,             setZone]              = React.useState('')

  const [saving,      setSaving]      = React.useState(false)
  const [confirmShow, setConfirmShow] = React.useState(false)

  const loadCustomers = React.useCallback(async () => {
    setLoading(true)
    try {
      const list = await getAllCustomers('reg')
      const pending = list.filter((c) => c.meta.status === 'รอการตรวจสอบ' || c.meta.status === 'pending')
      setCustomers(pending.sort((a, b) => b.meta.updatedAt - a.meta.updatedAt))
    } catch (e: any) {
      toastDanger(e?.message || 'โหลดไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => { loadCustomers() }, [loadCustomers])

  const handleSelect = (c: CustomerReg) => {
    setSelected(c)
    setActualAmount(String(c.loan.wantedAmount || ''))
    setInstallmentDays(String(c.loan.installmentDays || 30))
    setPurpose(c.loan.purpose || '')
    setZone(Array.isArray(c.loan.zone) ? c.loan.zone.join('+') : (c.loan.zone || ''))
    setScores(null)
    // load scores
    dbRead(`${DB_SCORES}${c.key}`).then((s) => setScores(s)).catch(() => {})
  }

  // calculated loan preview
  const loanCalc = React.useMemo(() => {
    const amt  = parseFloat(actualAmount.replace(/,/g, '')) || 0
    const rate = parseFloat(interestRate) || 0
    const days = parseInt(installmentDays) || 30
    if (!amt || !days) return null
    return calcLoan(amt, rate, days)
  }, [actualAmount, interestRate, installmentDays])

  const handleApprove = async () => {
    if (!selected || !loanCalc) return
    setSaving(true)
    try {
      const loan: LoanData = {
        actualAmount:      loanCalc.actualAmount,
        wantedAmount:      selected.loan.wantedAmount,
        interestRate:      parseFloat(interestRate) || 0,
        interestAmount:    loanCalc.interestAmount,
        totalWithInterest: loanCalc.totalWithInterest,
        dailyPayment:      loanCalc.dailyPayment,
        installmentDays:   parseInt(installmentDays),
        payoutAmount:      loanCalc.payoutAmount,
        zone,
        purpose,
        updatedAt: Date.now(),
        updatedBy: uid,
      }
      await approveCustomer(uid, selected.key, selected, loan)
      toastSuccess(`อนุมัติสินเชื่อ ${selected.personal.fullName} เรียบร้อย`)
      setSelected(null)
      await loadCustomers()
    } catch (e: any) {
      toastDanger(e?.message || 'อนุมัติไม่สำเร็จ')
    } finally {
      setSaving(false)
      setConfirmShow(false)
    }
  }

  if (loading) {
    return (
      <div className='d-flex justify-content-center align-items-center' style={{ minHeight: 300 }}>
        <Spinner animation='border' variant='warning' />
      </div>
    )
  }

  return (
    <div className='p-3'>
      <ConfirmAlert
        show={confirmShow}
        title='ยืนยันการอนุมัติสินเชื่อ'
        message={`ยืนยันอนุมัติสินเชื่อให้ ${selected?.personal.fullName || ''} ยอด ${formatCurrency(loanCalc?.actualAmount || 0)} กีบ`}
        confirmLabel={saving ? 'กำลังบันทึก...' : 'ยืนยันอนุมัติ'}
        variant='success'
        onConfirm={handleApprove}
        onCancel={() => setConfirmShow(false)}
      />

      <div className='d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2'>
        <h5 className='mb-0'>ฟอร์มอนุมัติสินเชื่อ</h5>
        <Badge bg='warning' text='dark'>{customers.length} รายการ</Badge>
      </div>

      <Row className='g-3'>
        {/* ─── Left: customer selector ─────────────────────────── */}
        <Col xs={12} md={selected ? 5 : 12}>
          <Card className='shadow-sm'>
            <Card.Header style={{ fontSize: 13, fontWeight: 700 }}>เลือกลูกค้า</Card.Header>
            <Card.Body style={{ padding: 0, maxHeight: 500, overflowY: 'auto' }}>
              {customers.length === 0 && (
                <div className='p-3 text-muted' style={{ fontSize: 13 }}>ไม่มีลูกค้ารอพิจารณา</div>
              )}
              {customers.map((c) => (
                <div
                  key={c.key}
                  onClick={() => handleSelect(c)}
                  style={{
                    padding: '10px 14px',
                    borderBottom: '1px solid #f0f0f0',
                    cursor: 'pointer',
                    backgroundColor: selected?.key === c.key ? '#fffbf0' : 'transparent',
                    borderLeft: selected?.key === c.key ? '3px solid #ffc107' : '3px solid transparent',
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{c.personal.fullName}</div>
                  <div style={{ fontSize: 11, color: '#888' }}>
                    {c.personal.phone} | {formatDate(c.meta.createdAt)}
                  </div>
                  <div style={{ fontSize: 11, marginTop: 2 }}>
                    วงเงิน: <strong>{formatCurrency(c.loan.wantedAmount)}</strong> กีบ |{' '}
                    {c.loan.installmentDays} วัน | โซน: {Array.isArray(c.loan.zone) ? c.loan.zone.join('+') : c.loan.zone}
                  </div>
                </div>
              ))}
            </Card.Body>
          </Card>
        </Col>

        {/* ─── Right: approve form ──────────────────────────────── */}
        {selected && (
          <Col xs={12} md={7}>
            <Row className='g-3'>
              {/* ข้อมูลลูกค้า */}
              <Col xs={12}>
                <Card className='shadow-sm'>
                  <Card.Header style={{ fontSize: 13, fontWeight: 700 }}>
                    ข้อมูลการสมัคร
                    <Badge bg='warning' text='dark' className='ms-2' style={{ fontSize: 10 }}>
                      คะแนนรวม {scores?.totalPoint ?? 0}
                    </Badge>
                  </Card.Header>
                  <Card.Body style={{ maxHeight: 200, overflowY: 'auto' }}>
                    <div className='mb-2'>
                    <PhotoGallery customerId={selected.key} photoCount={selected.meta.photoCount} />
                  </div>
                  <Table size='sm' style={{ fontSize: 12, marginBottom: 0 }}>
                      <tbody>
                        <tr><td style={{ width: '40%', fontWeight: 600 }}>ชื่อ - นามสกุล</td><td>{selected.personal.fullName}</td></tr>
                        <tr><td style={{ fontWeight: 600 }}>เลขบัตร</td><td>{selected.personal.nationalId || '-'}</td></tr>
                        <tr><td style={{ fontWeight: 600 }}>วันเกิด</td><td>{selected.personal.birthDate || '-'}</td></tr>
                        <tr><td style={{ fontWeight: 600 }}>อายุ</td><td>{selected.personal.age || '-'}</td></tr>
                        <tr><td style={{ fontWeight: 600 }}>โทรศัพท์</td><td>{selected.personal.phone || '-'}</td></tr>
                        <tr><td style={{ fontWeight: 600 }}>ที่อยู่</td><td>{selected.personal.address || '-'}</td></tr>
                        <tr><td style={{ fontWeight: 600 }}>รายได้/วัน</td><td>{formatCurrency(selected.finance.dailyIncome)}</td></tr>
                        <tr><td style={{ fontWeight: 600 }}>วงเงินที่ขอ</td><td>{formatCurrency(selected.loan.wantedAmount)} กีบ</td></tr>
                        <tr><td style={{ fontWeight: 600 }}>งวด</td><td>{selected.loan.installmentDays} วัน</td></tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>

              {/* กรอกยอดจริง */}
              <Col xs={12}>
                <Card className='shadow-sm'>
                  <Card.Header style={{ fontSize: 13, fontWeight: 700 }}>กรอกยอดอนุมัติจริง</Card.Header>
                  <Card.Body>
                    <Row className='g-2'>
                      <Col xs={12} md={6}>
                        <Form.Group>
                          <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>ยอดกู้จริง (กีบ) *</Form.Label>
                          <Form.Control
                            size='sm' type='number'
                            value={actualAmount}
                            onChange={(e) => setActualAmount(e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Group>
                          <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>ดอกเบี้ย (%)</Form.Label>
                          <Form.Control
                            size='sm' type='number'
                            value={interestRate}
                            onChange={(e) => setInterestRate(e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Group>
                          <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>จำนวนวัน/เดือน *</Form.Label>
                          <Form.Control
                            size='sm' type='number'
                            value={installmentDays}
                            onChange={(e) => setInstallmentDays(e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Group>
                          <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>โซน</Form.Label>
                          <Form.Control
                            size='sm' type='text'
                            value={zone}
                            onChange={(e) => setZone(e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={12}>
                        <Form.Group>
                          <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>วัตถุประสงค์</Form.Label>
                          <Form.Control
                            size='sm' as='textarea' rows={2}
                            value={purpose}
                            onChange={(e) => setPurpose(e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>

              {/* Preview */}
              {loanCalc && (
                <Col xs={12}>
                  <Card className='shadow-sm' style={{ borderColor: '#ffc107' }}>
                    <Card.Header style={{ fontSize: 13, fontWeight: 700, backgroundColor: '#fffbf0' }}>
                      ตัวอย่างยอดสรุป
                    </Card.Header>
                    <Card.Body>
                      <Table size='sm' style={{ fontSize: 13, marginBottom: 0 }}>
                        <tbody>
                          <tr>
                            <td style={{ fontWeight: 600 }}>ยอดกู้</td>
                            <td className='text-end'>{formatCurrency(loanCalc.actualAmount)}</td>
                          </tr>
                          <tr>
                            <td style={{ fontWeight: 600 }}>ดอกเบี้ย</td>
                            <td className='text-end'>{formatCurrency(loanCalc.interestAmount)}</td>
                          </tr>
                          <tr>
                            <td style={{ fontWeight: 600 }}>ยอดรวม</td>
                            <td className='text-end'>{formatCurrency(loanCalc.totalWithInterest)}</td>
                          </tr>
                          <tr>
                            <td style={{ fontWeight: 600 }}>ยอดส่ง/{installmentDays} วัน</td>
                            <td className='text-end'>{formatCurrency(loanCalc.dailyPayment)}</td>
                          </tr>
                          <tr style={{ backgroundColor: '#e8f5e9' }}>
                            <td style={{ fontWeight: 700 }}>หักงวดแรก → เงินโอนให้ลูกค้า</td>
                            <td className='text-end' style={{ fontWeight: 700, color: '#2e7d32' }}>
                              {formatCurrency(loanCalc.payoutAmount)} กีบ
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>
              )}

              <Col xs={12}>
                <div className='d-flex gap-2 justify-content-end'>
                  <Button variant='outline-secondary' size='sm' onClick={() => setSelected(null)}>
                    ยกเลิก
                  </Button>
                  <Button
                    variant='success'
                    size='sm'
                    disabled={!loanCalc || saving}
                    onClick={() => setConfirmShow(true)}
                  >
                    <i className='fa-solid fa-check me-1' />อนุมัติสินเชื่อ
                  </Button>
                </div>
              </Col>
            </Row>
          </Col>
        )}
      </Row>
    </div>
  )
}